import { Action } from '@interstate/components/Action';
import { useOrderAgnosticILDPURL } from '@makemydeal/dr-dash-components';
import * as creditDecisionActionCreators from '@makemydeal/dr-dash-store/actions/creditDecisionActionCreators';
import * as fullLenderDecisionsActionCreators from '@makemydeal/dr-dash-store/actions/fullLenderDecisionsActionCreators';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as fullLenderDecisionsSelectors from '@makemydeal/dr-dash-store/selectors/fullLenderDecisionsSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { setAuthTokenCookie } from '@makemydeal/dr-shared-ui-utils';

import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type ViewLenderDecisionLinkProps = {
    isInsideSlideOut?: boolean;
};

export const ViewLenderDecisionLink = ({ isInsideSlideOut = false }: ViewLenderDecisionLinkProps) => {
    const dispatch = useDispatch();
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const ildpDetailsLink = useSelector(dealXgDetailsSelectors.getIldpDetailsLink);
    const orderDetailsLink = useSelector(dealXgDetailsSelectors.getOrderDetailsLink);
    const enableOrderAgnosticILDPUI = useSelector(featureToggleSelectors.enableOrderAgnosticILDPUI);
    const enableCreditBureauAndDecisionPBCRouteOne = useSelector(featureToggleSelectors.enableCreditBureauAndDecisionPBCRouteOne);
    const isCreditProviderRouteOne = useSelector(dealerSelectors.isCreditProviderRouteOne);
    const isLoadingLenderDecisions = useSelector(fullLenderDecisionsSelectors.getIsLoadingFullLenderDecisions);
    const isLenderDecisionFromFSEnabled = useSelector(featureToggleSelectors.isEnableMVLenderDecisions);
    const isFldFinanceMethodSameAsCurrentOfferType = useSelector(
        fullLenderDecisionsSelectors.isFldFinanceMethodSameAsCurrentOfferType
    );

    const [isGoingToNavigateToCreditDecision, setIsGoingToNavigateToCreditDecision] = useState(false);
    const oktaContext = useOktaAuth();
    const orderAgnosticILDPURL = useOrderAgnosticILDPURL();

    const shouldNavigateToCreditDecision =
        !isCreditProviderRouteOne || (enableCreditBureauAndDecisionPBCRouteOne && isCreditProviderRouteOne);

    const getJwtToken = () => oktaContext?.oktaAuth.getAccessToken() || '';

    const navigateToCreditDecision = () => dispatch(navigationActionCreators.navigateToCreditDecision());

    const navigateToCreditBureau = () => dispatch(navigationActionCreators.navigateToCreditBureau());

    const openCreditDecisionSlideOut = () => dispatch(creditDecisionActionCreators.openCreditDecisionSlideOut());

    const handleViewLenderDecisionClick = () => {
        if (isEcommerceDeal) {
            const token = getJwtToken();
            const orderLink = ildpDetailsLink || `${orderDetailsLink}?show=ildp`;
            if (ildpDetailsLink) {
                setAuthTokenCookie(token);
            }
            window.open(`${orderLink}`, '_blank');
        } else if (!isEcommerceDeal && enableOrderAgnosticILDPUI && orderAgnosticILDPURL) {
            const token = getJwtToken();
            setAuthTokenCookie(token);
            window.open(`${orderAgnosticILDPURL}`, '_blank');
        } else if (isInsideSlideOut && shouldNavigateToCreditDecision) {
            openCreditDecisionSlideOut();
        } else if (shouldNavigateToCreditDecision) {
            if (isLenderDecisionFromFSEnabled && isFldFinanceMethodSameAsCurrentOfferType) {
                setIsGoingToNavigateToCreditDecision(true);
                dispatch(fullLenderDecisionsActionCreators.fetchLenderDecisions());
            } else {
                navigateToCreditDecision();
            }
        } else {
            navigateToCreditBureau();
        }
    };

    useEffect(() => {
        if (!isLenderDecisionFromFSEnabled || !isFldFinanceMethodSameAsCurrentOfferType) {
            return;
        }

        if (isGoingToNavigateToCreditDecision && !isLoadingLenderDecisions) {
            navigateToCreditDecision();
        }
    }, [isGoingToNavigateToCreditDecision, isLoadingLenderDecisions, isLenderDecisionFromFSEnabled]);

    return (
        <Action size="sm" data-testid="lender-view-lender-decision" onClick={handleViewLenderDecisionClick}>
            View Lender Decisions
        </Action>
    );
};
